import { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from './Button';
import IconPdf from '../public/images/pdf.svg';
import IconWord from '../public/images/word.svg';
import IconExcel from '../public/images/excel.svg';
import IconHtml from '../public/images/html.svg';
import IconJpg from '../public/images/jpg.svg';
import { sendEvent } from '../utils/analytics';

const apps = {
  pdf: {
    icon: IconPdf,
    extension: 'PDF',
    accept: [
      '.pdf',
      'application/pdf',
    ].join(','),
  },
  word: {
    icon: IconWord,
    extension: 'DOCX',
    accept: [
      '.doc',
      '.docx',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].join(','),
  },
  excel: {
    icon: IconExcel,
    extension: 'XLSX',
    accept: [
      '.xls',
      '.xlsx',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].join(','),
  },
  html: {
    icon: IconHtml,
    extension: 'HTML',
    accept: [
      '.html',
      'text/html',
    ].join(','),
  },
  jpg: {
    icon: IconJpg,
    extension: 'JPG',
    accept: [
      '.jpg',
      '.jpeg',
      'image/jpeg',
    ].join(','),
  },
};

const Dropzone = ({
  className,
  type,
  disabled,
  options,
  onlyButton,
  buttonProps,
  buttonText,
  onAccepted,
  ...props
}) => {
  const { t } = useTranslation('convert-and-edit');
  const { icon: Icon, extension, accept } = apps[type] || {};

  const handleDropAccepted = useCallback((acceptedFiles) => {
    if (disabled) {
      return;
    }

    onAccepted?.(acceptedFiles);
  }, [disabled, onAccepted]);

  const maxFiles = options?.maxFiles > 0 ? options.maxFiles : 1;
  const multiple = options?.multiple || maxFiles > 1;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    ...options,
    maxFiles,
    multiple,
    disabled,
    onDropAccepted: handleDropAccepted,
  });

  useEffect(() => {
    const regularBtn = document.querySelector('.dropzone__button');
    if (regularBtn) {
      regularBtn.addEventListener('click', () => {
        sendEvent({
          category: 'choose-file',
          action: 'click',
          label: window.location.pathname.slice(1),
        });
      });
    }
  }, []);

  return (
    <div {...getRootProps({
      className: classNames(
        'dropzone',
        isDragActive && 'dropzone--active',
        onlyButton && 'dropzone--only-button',
        className,
      ),
      tabIndex: -1,
      ...props,
    })}>
      <input {...getInputProps({ className: 'dropzone__input' })} />
      <div className="dropzone__button-container">
        <Button
          className="dropzone__button"
          size="md"
          variant="primary"
          {...buttonProps}
          disabled={disabled}
        >
          <div className="dropzone__button-content">
            <Icon className="dropzone__icon" aria-hidden="true" />
            {buttonText || t('convert-and-edit:dropzone.button', { format: extension })}
          </div>
        </Button>
      </div>
      {!onlyButton && extension && (
        <div className="dropzone__text">
          {t('convert-and-edit:dropzone.text', { format: extension })}
        </div>
      )}
    </div>
  );
};

Dropzone.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf([
    'pdf',
    'word',
    'excel',
    'html',
    'jpg',
  ]).isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.object,
  onlyButton: PropTypes.bool,
  buttonProps: PropTypes.object,
  buttonText: PropTypes.string,
  onAccepted: PropTypes.func,
};

export default Dropzone;
